import { View } from '@zinc/ui';

import HeidiTooltip from 'bz-clinical/heidi/heidi-tooltip';

import useComputed from 'bz-utilities/hooks/computed';

import cx from 'classnames';

import { observer } from 'mobx-react';

import React from 'react';

import type { IProps } from '.';
import { toPick } from './data';
import { getWidthFromTextSize } from './utils';

const DraftEnityControlBody = observer(
  Object.assign((props: IProps) => {
    const {
      aiFilledFields,
      attributes,
      control,
      children,
      defaults,
      draggable,
      field,
      inline,
      readOnly,
      onDragStart,
      onSelect
    } = props;
    const renderControl = control?.default ?? control; //some controls not being imported correctly - to be cleaned up in future

    const childProps: any = Object.assign(
      props.bzPick(...toPick), // slight hack for table
      attributes,
      defaults,
      attributes.tableConfig
    );

    const aiGenerated = useComputed((): boolean | undefined => {
      return !readOnly && aiFilledFields?.has(field);
    }, [aiFilledFields, field, readOnly]);

    const className = React.useMemo(() => {
      let styles;
      if (children?.length === 1) {
        let { styleSet } = children[0].props;
        if (styleSet.size > 0) {
          styles = styleSet._map._list._tail.array
            .filter(BzNotFalsy)
            .map(([x]: string[]) => `entity-${x.toLowerCase()}`);
        }
      }

      const hidden = !!attributes.hiddenfield && !draggable;

      return cx('entity', styles, {
        'entity-inline': inline,
        'entity-block': !inline,
        'entity-hidden': hidden,
        'p-2  m-2 border-purple-900 border-4 rounded-xl border-solid': aiGenerated
      });
    }, [children, inline, draggable, aiGenerated, attributes.hiddenfield]);

    const style: React.CSSProperties = React.useMemo(() => {
      return {
        width: getWidthFromTextSize(childProps.textSize)
      };
    }, [childProps.textSize]);

    const _onDragStart: React.DragEventHandler<HTMLSpanElement> = React.useCallback(
      (e) => {
        onDragStart?.(e);
      },
      [onDragStart]
    );

    const _onSelect: React.MouseEventHandler<HTMLDivElement> = React.useCallback(
      (e) => {
        e.preventDefault();
        e.stopPropagation();
        onSelect?.();
      },
      [onSelect]
    );
    let content = (
      <>
        <div className="entity-children">{children}</div>
        <div contentEditable={false} className="entity-content">
          {React.createElement(renderControl, childProps)}
          <div className="entity-mask" onClick={_onSelect} />
        </div>
      </>
    );

    if (aiGenerated) {
      content = (
        <View direction="row" justify="space-between">
          <div>{content}</div>
          <div className="p-2">
            <HeidiTooltip>This field was filled in by Heidi</HeidiTooltip>
          </div>
        </View>
      );
    }

    return (
      <span className={className} draggable={draggable} style={style} onDragStart={_onDragStart}>
        {content}
      </span>
    );
  }, 'DraftEnityControlBody')
);

export default DraftEnityControlBody;
